import { Fragment, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CircleMarker, MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import config from "../config";
import { useTranslation } from "react-i18next";

const createCareMapIcon = ({ className, label, title }) =>
  L.divIcon({
    className: "care-map-marker-shell",
    html: `
      <div class="care-map-marker ${className}" aria-label="${title}">
        <span>${label}</span>
      </div>
    `,
    iconSize: [46, 54],
    iconAnchor: [23, 50],
    popupAnchor: [0, -48]
  });

const greenIcon = createCareMapIcon({
  className: "care-map-marker-available",
  label: "A",
  title: "Available caregiver"
});

const redIcon = createCareMapIcon({
  className: "care-map-marker-unavailable",
  label: "U",
  title: "Unavailable caregiver"
});

const blackIcon = createCareMapIcon({
  className: "care-map-marker-elder",
  label: "E",
  title: "Elder location"
});

const COUNTRY_ABBREVIATIONS = {
  Uganda: "UG",
  Kenya: "KE",
  USA: "US",
  UK: "UK",
  Canada: "CA",
  India: "IN"
};

const getCountryAbbreviation = (countryName = "") => {
  if (COUNTRY_ABBREVIATIONS[countryName]) {
    return COUNTRY_ABBREVIATIONS[countryName];
  }

  const words = String(countryName).trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return words.slice(0, 2).map((word) => word[0]).join("").toUpperCase();
  }

  return String(countryName).slice(0, 2).toUpperCase() || "GL";
};

const createCountryClusterIcon = (countryName, count) =>
  L.divIcon({
    className: "country-cluster-icon-wrapper",
    html: `
      <div class="country-cluster-icon" aria-label="${countryName}: ${count} caregivers">
        <span class="country-cluster-code">${getCountryAbbreviation(countryName)}</span>
        <span class="country-cluster-count">${count}</span>
        <span class="country-cluster-caption">caregivers</span>
      </div>
    `,
    iconSize: [62, 62],
    iconAnchor: [31, 31]
  });

const FILTER_TYPES = [
  { label: "Language", value: "language" },
  { label: "Gender", value: "gender" },
  { label: "Country", value: "country" },
  { label: "Rate per hour", value: "rate" }
];

const LANGUAGE_OPTIONS = ["English", "Spanish", "French", "Swahili", "Arabic", "Portuguese", "Chinese", "Hindi", "German"];

const APPOINTMENT_HOURS = Array.from({ length: 24 }, (_, index) =>
  String(index).padStart(2, "0")
);
const APPOINTMENT_MINUTES = Array.from({ length: 60 }, (_, index) =>
  String(index).padStart(2, "0")
);
const DEFAULT_APPOINTMENT_HOUR = "09";
const DEFAULT_APPOINTMENT_MINUTE = "00";

const splitAppointmentDateTime = (value = "") => {
  const [date = "", time = ""] = String(value).split("T");
  const [hour = "", minute = ""] = time.split(":");

  return {
    date,
    hour,
    minute
  };
};

const buildAppointmentDateTime = (date, hour, minute) => {
  if (!date) return "";

  return `${date}T${hour || DEFAULT_APPOINTMENT_HOUR}:${minute || DEFAULT_APPOINTMENT_MINUTE}`;
};
const GENDER_OPTIONS = [
  { label: "Male", value: "M" },
  { label: "Female", value: "F" }
];
const COUNTRY_OPTIONS = ["Uganda", "Kenya", "USA", "UK", "Canada", "India"];
const RATE_OPTIONS = [...Array(20)].map((_, i) => i + 1);
const APPOINTMENT_ALERT_WINDOW_MS = 15 * 60 * 1000;
const APPOINTMENT_SOON_WINDOW_MS = 10 * 60 * 1000;
const ELDER_ALERT_STORAGE_KEY = "ubercare-elder-appointment-alerts";

const getFilterOptions = (filterType) => {
  if (filterType === "language") {
    return LANGUAGE_OPTIONS.map((language) => ({ label: language, value: language }));
  }

  if (filterType === "gender") {
    return GENDER_OPTIONS;
  }

  if (filterType === "country") {
    return COUNTRY_OPTIONS.map((country) => ({ label: country, value: country }));
  }

  if (filterType === "rate") {
    return RATE_OPTIONS.map((rate) => ({
      label: `$${rate}/hr`,
      value: rate
    }));
  }

  return [];
};

const compactFilters = (nextFilters) =>
  Object.fromEntries(
    Object.entries(nextFilters).filter(([, value]) => String(value || "").trim())
  );

const getFilterValueLabel = (filterType, value) =>
  getFilterOptions(filterType).find((option) => String(option.value) === String(value))?.label ||
  value;

const getAppointmentTime = (appointment = {}) => {
  const value = appointment.scheduledForText || appointment.scheduledFor;
  const parsed = value ? new Date(value) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed.getTime() : null;
};

const getAppointmentAlertStatus = (appointment, now = Date.now()) => {
  const appointmentTime = getAppointmentTime(appointment);
  if (!appointmentTime) return "";

  const delta = appointmentTime - now;
  if (delta <= 0 && Math.abs(delta) <= APPOINTMENT_ALERT_WINDOW_MS) return "now";
  if (delta > 0 && delta <= APPOINTMENT_SOON_WINDOW_MS) return "soon";
  return "";
};

const createAppointmentAlertMessage = (appointment) => {
  const caregiverName = `${appointment.workerFirstName || ""} ${appointment.workerLastName || ""}`
    .replace(/\s+/g, " ")
    .trim() || "your caregiver";
  const timeText =
    appointment.patientScheduledForText ||
    formatAppointmentDate(appointment.scheduledForText || appointment.scheduledFor);

  return `It is time for your WhatsApp care call with ${caregiverName}. Scheduled time: ${timeText}.`;
};

const readAlertedAppointments = (storageKey) => {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(storageKey) || "[]"));
  } catch {
    return new Set();
  }
};

const rememberAlertedAppointment = (storageKey, appointmentID, alertedSet) => {
  alertedSet.add(appointmentID);
  try {
    sessionStorage.setItem(storageKey, JSON.stringify([...alertedSet]));
  } catch {
    // Session storage can be unavailable in strict privacy modes.
  }
};

const statusMeta = {
  available: {
    label: "Available now",
    tone: "#15803d",
    chipBackground: "rgba(21, 128, 61, 0.12)"
  },
  unavailable: {
    label: "Currently unavailable",
    tone: "#b91c1c",
    chipBackground: "rgba(185, 28, 28, 0.12)"
  }
};

const getYouTubeEmbedUrl = (value) => {
  if (!value) return "";

  try {
    const url = new URL(value);
    const hostname = url.hostname.replace(/^www\./, "").toLowerCase();

    if (hostname === "youtu.be") {
      const id = url.pathname.split("/").filter(Boolean)[0];
      return id ? `https://www.youtube.com/embed/${id}` : "";
    }

    if (hostname.endsWith("youtube.com")) {
      if (url.pathname === "/watch") {
        const id = url.searchParams.get("v");
        return id ? `https://www.youtube.com/embed/${id}` : "";
      }

      const [, type, id] = url.pathname.split("/");
      if ((type === "shorts" || type === "embed") && id) {
        return `https://www.youtube.com/embed/${id}`;
      }
    }
  } catch {
    return "";
  }

  return "";
};

const buildLocationParts = (locationOwner) =>
  [
    locationOwner.city,
    locationOwner.district || locationOwner.stateProvince,
    locationOwner.country
  ].filter(Boolean);

const getLocationLabel = (locationOwner) => buildLocationParts(locationOwner).join(", ");

const getWorkerDisplayName = (worker = {}) =>
  `${worker.firstName || ""} ${worker.lastName || ""}`.replace(/\s+/g, " ").trim() ||
  "Caregiver";

const getStableMarkerKey = (item = {}) =>
  String(item.markerID || item.workerID || item.patientID || item.country || getWorkerDisplayName(item));

const getCoordinateKey = (latitude, longitude) =>
  `${Number(latitude).toFixed(6)}:${Number(longitude).toFixed(6)}`;

const getOffsetPosition = (latitude, longitude, index, total) => {
  if (total <= 1) {
    return [Number(latitude), Number(longitude)];
  }

  const angle = (Math.PI * 2 * index) / total;
  const radius = 0.008 + Math.floor(index / 8) * 0.004;
  const safeLatitude = Number(latitude);
  const latitudeOffset = Math.sin(angle) * radius;
  const longitudeOffset =
    (Math.cos(angle) * radius) / Math.max(Math.cos((safeLatitude * Math.PI) / 180), 0.35);

  return [safeLatitude + latitudeOffset, Number(longitude) + longitudeOffset];
};

const spreadOverlappingMarkers = (items) => {
  const grouped = items.reduce((acc, item) => {
    const key = getCoordinateKey(item.latitude, item.longitude);

    if (!acc[key]) {
      acc[key] = [];
    }

    acc[key].push(item);
    return acc;
  }, {});

  return Object.values(grouped).flatMap((group) =>
    [...group].sort((left, right) =>
      getStableMarkerKey(left).localeCompare(getStableMarkerKey(right))
    ).map((item, index) => {
      const overlapGroupKey = getCoordinateKey(item.latitude, item.longitude);
      const [latitude, longitude] = getOffsetPosition(
        item.latitude,
        item.longitude,
        index,
        group.length
      );

      return {
        ...item,
        markerLatitude: latitude,
        markerLongitude: longitude,
        overlapCount: group.length,
        overlapIndex: index,
        originLatitude: Number(item.latitude),
        originLongitude: Number(item.longitude),
        overlapGroupKey,
        isOverlapping: group.length > 1
      };
    })
  );
};

const buildOverlapGuides = (markers) =>
  Object.values(
    markers.reduce((acc, marker) => {
      if (marker.markerType !== "worker" || !marker.isOverlapping) {
        return acc;
      }

      if (!acc[marker.overlapGroupKey]) {
        acc[marker.overlapGroupKey] = {
          key: marker.overlapGroupKey,
          count: marker.overlapCount,
          center: [marker.originLatitude, marker.originLongitude],
          members: []
        };
      }

      acc[marker.overlapGroupKey].members.push(marker);
      return acc;
    }, {})
  );

const buildCountryClusters = (workers) =>
  Object.values(
    workers.reduce((acc, worker) => {
      const countryName = worker.country || "Other";
      if (!acc[countryName]) {
        acc[countryName] = {
          country: countryName,
          members: []
        };
      }

      acc[countryName].members.push(worker);
      return acc;
    }, {})
  ).map((cluster) => {
    const center = cluster.members.reduce(
      (sum, worker) => ({
        latitude: sum.latitude + Number(worker.latitude),
        longitude: sum.longitude + Number(worker.longitude)
      }),
      { latitude: 0, longitude: 0 }
    );

    return {
      ...cluster,
      latitude: center.latitude / cluster.members.length,
      longitude: center.longitude / cluster.members.length
    };
  });

const ZoomControls = () => {
  const map = useMap();

  return (
    <div style={zoomControls}>
      <button type="button" style={zoomBtn} onClick={() => map.zoomIn()}>
        +
      </button>
      <button type="button" style={zoomBtn} onClick={() => map.zoomOut()}>
        -
      </button>
    </div>
  );
};

const MapViewportController = ({ focusPoints }) => {
  const map = useMap();

  useEffect(() => {
    if (!Array.isArray(focusPoints) || focusPoints.length === 0) return;

    if (focusPoints.length === 1) {
      map.flyTo(focusPoints[0], 6, { duration: 0.8 });
      return;
    }

    map.flyToBounds(L.latLngBounds(focusPoints), {
      padding: [48, 48],
      maxZoom: 7,
      duration: 0.8
    });
  }, [focusPoints, map]);

  return null;
};

const WorkerPopup = ({
  worker,
  loadingWorkerID,
  onToggleActivation,
  onSchedule,
  schedulerWorkerID,
  schedulerProps,
  onWatchIntro,
  renderStars,
  t
}) => {
  const [showMore, setShowMore] = useState(false);
  const isAvailable = worker.status === "available";
  const meta = isAvailable ? statusMeta.available : statusMeta.unavailable;
  const initials = `${worker.firstName?.[0] || ""}${worker.lastName?.[0] || ""}`.toUpperCase();
  const location = getLocationLabel(worker);
  const whatsappNumber = String(worker.whatsapp || "").replace(/[^\d]/g, "");
  const genderLabel =
    worker.gender === "M"
      ? "Male"
      : worker.gender === "F"
        ? "Female"
        : worker.gender || t("map:popup.notListed");
  const rateLabel =
    worker.rate || worker.rate === 0 ? `$${worker.rate}/hr` : t("map:popup.notListed");
  const specialties = typeof worker.specialties === "string"
    ? worker.specialties
    : Array.isArray(worker.specialties)
      ? worker.specialties.filter(Boolean).join(", ")
      : "";
  const certifications = typeof worker.certifications === "string"
    ? worker.certifications
    : Array.isArray(worker.certifications)
      ? worker.certifications.filter(Boolean).join(", ")
      : "";
  const experienceHighlights = Array.isArray(worker.experienceEntries)
    ? worker.experienceEntries.filter(Boolean)
    : [];
  const isApproximateLocation =
    worker.locationPrecision &&
    !["city/district", "district"].includes(worker.locationPrecision);

  return (
    <div className="worker-popup-card">
      <div className="worker-popup-hero">
        {worker.profilePhoto ? (
          <img
            src={worker.profilePhoto}
            alt={`${worker.firstName || ""} ${worker.lastName || ""}`.trim() || "Worker"}
            className="worker-avatar worker-photo"
          />
        ) : (
          <div className="worker-avatar">{initials || "CW"}</div>
        )}
        <div>
          <div className="worker-name">
            {worker.firstName} {worker.lastName}
          </div>
          <div className="worker-role">
            {worker.professionalTitle || t("map:popup.remoteCaregiver")}
          </div>
        </div>
        <span
          className="worker-status-chip"
          style={{ color: meta.tone, background: meta.chipBackground }}
        >
          {meta.label}
        </span>
      </div>

      <div className="worker-popup-grid">
        <div className="worker-popup-stat">
          <span>{t("map:popup.language")}</span>
          <strong>{worker.spokenLanguage || t("map:popup.notListed")}</strong>
        </div>
        <div className="worker-popup-stat">
          <span>{t("map:popup.gender")}</span>
          <strong>{genderLabel}</strong>
        </div>
        <div className="worker-popup-stat">
          <span>{t("map:popup.rate")}</span>
          <strong>{rateLabel}</strong>
        </div>
        <div className="worker-popup-stat">
          <span>{t("map:popup.location")}</span>
          <strong>{location || t("map:popup.locationNotProvided")}</strong>
          {isApproximateLocation && (
            <small className="worker-popup-location-note">
              Map pin is approximate at {worker.locationPrecision} level.
            </small>
          )}
        </div>
        <div className="worker-popup-stat">
          <span>{t("map:popup.contact")}</span>
          <strong>{worker.whatsapp || t("map:popup.phoneNotProvided")}</strong>
        </div>
      </div>

      <div className="worker-popup-section">
        <div className="worker-popup-label">{t("map:popup.familyFeedback")}</div>
        <div className="worker-rating-row">
          <div>{renderStars(worker.rating)}</div>
          <span className="worker-rating-copy">{t("map:popup.ratingValue", { rating: Number(worker.rating) || 0 })}</span>
        </div>
      </div>

      {worker.introVideoUrl && (
        <button
          type="button"
          className="worker-secondary-action"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onWatchIntro(worker);
          }}
        >
          Watch caregiver intro
        </button>
      )}

      <button
        type="button"
        className="worker-more-toggle"
        aria-expanded={showMore}
        onClick={() => setShowMore((prev) => !prev)}
      >
        <span>{showMore ? "Hide extra details" : "See full caregiver details"}</span>
        <span className={`worker-more-toggle-icon${showMore ? " is-open" : ""}`} aria-hidden="true">
          {showMore ? "˄" : "˅"}
        </span>
      </button>

      {showMore && worker.bio && (
        <div className="worker-popup-section">
          <div className="worker-popup-label">About</div>
          <div className="worker-popup-text">{worker.bio}</div>
        </div>
      )}

      {showMore && (
        <>
          <div className="worker-popup-grid worker-popup-grid-expanded">
            <div className="worker-popup-stat">
              <span>{t("map:popup.payment")}</span>
              <strong>{worker.paymentMethod || t("map:popup.flexible")}</strong>
            </div>
            <div className="worker-popup-stat">
              <span>Experience</span>
              <strong>
                {worker.yearsExperience ? `${worker.yearsExperience} years` : t("map:popup.notListed")}
              </strong>
            </div>
            <div className="worker-popup-stat">
              <span>Certifications</span>
              <strong>{certifications || t("map:popup.notListed")}</strong>
            </div>
            <div className="worker-popup-stat">
              <span>{t("map:popup.serviceArea")}</span>
              <strong>{location || t("map:popup.locationNotProvided")}</strong>
            </div>
          </div>

          {specialties && (
            <div className="worker-popup-section">
              <div className="worker-popup-label">Specialties</div>
              <div className="worker-popup-text">{specialties}</div>
            </div>
          )}

          {worker.availabilityNotes && (
            <div className="worker-popup-section">
              <div className="worker-popup-label">Availability notes</div>
              <div className="worker-popup-text">{worker.availabilityNotes}</div>
            </div>
          )}

          {experienceHighlights.length > 0 && (
            <div className="worker-popup-section">
              <div className="worker-popup-label">Work history</div>
              <div className="worker-popup-list">
                {experienceHighlights.map((entry, index) => (
                  <div key={`${worker.workerID}-exp-${index}`} className="worker-popup-list-item">
                    {typeof entry === "string" ? entry : entry?.title || entry?.role || entry?.summary || JSON.stringify(entry)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {isAvailable && whatsappNumber ? (
        <a
          href={`https://wa.me/${whatsappNumber}`}
          target="_blank"
          rel="noreferrer"
          className="worker-primary-action"
        >
          {t("map:popup.contactWhatsapp")}
        </a>
      ) : (
        <button type="button" disabled className="worker-secondary-action worker-disabled">
          {t("map:popup.workerUnavailableBtn")}
        </button>
      )}

      <button
        type="button"
        className="worker-secondary-action"
        style={{
          background: worker.activated
            ? "linear-gradient(135deg, #dc3545, #b91c1c)"
            : "linear-gradient(135deg, #15803d, #16a34a)",
          opacity: loadingWorkerID === worker.workerID ? 0.75 : 1
        }}
        disabled={loadingWorkerID === worker.workerID}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onToggleActivation(worker.workerID, worker.activated);
        }}
      >
        {loadingWorkerID === worker.workerID
          ? t("map:popup.updating")
          : worker.activated
            ? t("map:popup.deactivateWorker")
            : t("map:popup.activateWorker")}
      </button>

      {worker.activated && (
        <>
          <button
            type="button"
            className="worker-secondary-action"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onSchedule(worker);
            }}
          >
            Schedule WhatsApp call
          </button>

          {schedulerWorkerID === worker.workerID && schedulerProps && (
            <AppointmentScheduler
              {...schedulerProps}
              worker={worker}
              compact
              panelStyle={popupAppointmentPanel}
            />
          )}
        </>
      )}
    </div>
  );
};

const PatientPopup = ({ patient, t }) => {
  const location = getLocationLabel(patient);

  return (
    <div className="worker-popup-card patient-popup-card">
      <div className="worker-popup-hero">
        <div className="worker-avatar patient-avatar">{t("map:popup.you")}</div>
        <div>
          <div className="worker-name">
            {patient.firstName} {patient.lastName}
          </div>
          <div className="worker-role">{t("map:popup.yourBase")}</div>
        </div>
      </div>

      <div className="worker-popup-grid">
        <div className="worker-popup-stat">
          <span>{t("map:popup.language")}</span>
          <strong>{patient.spokenLanguage || t("map:popup.notListed")}</strong>
        </div>
        <div className="worker-popup-stat">
          <span>{t("map:popup.location")}</span>
          <strong>{location || t("map:popup.locationUnknown")}</strong>
        </div>
      </div>
    </div>
  );
};

const AppointmentNotifications = ({ appointments, urgentAppointmentID, alertMessage }) => (
  <div style={appointmentPanel}>
    <div style={appointmentEyebrow}>Appointments</div>
    <h3 style={appointmentTitle}>Caregiver call notifications</h3>

    {"Notification" in window && Notification.permission === "default" && (
      <button
        type="button"
        style={enableAlertsButton}
        onClick={() => Notification.requestPermission()}
      >
        Enable appointment alerts
      </button>
    )}

    {alertMessage && <div style={appointmentAlertBox}>{alertMessage}</div>}

    {appointments.length ? (
      <div style={appointmentList}>
        {appointments.slice(0, 4).map((appointment) => {
          const whatsappNumber = String(appointment.workerWhatsapp || "").replace(/[^\d]/g, "");
          const alertStatus = getAppointmentAlertStatus(appointment);

          return (
            <div
              key={appointment.appointmentID}
              style={{
                ...appointmentCard,
                ...(appointment.appointmentID === urgentAppointmentID ? urgentAppointmentCard : {}),
                ...(alertStatus === "soon" ? soonAppointmentCard : {})
              }}
            >
              {appointment.appointmentID === urgentAppointmentID && (
                <span style={urgentBadge}>Time for appointment</span>
              )}
              {alertStatus === "soon" && (
                <span style={soonBadge}>Starting soon</span>
              )}
              <strong style={appointmentCaregiver}>
                {appointment.workerFirstName} {appointment.workerLastName}
              </strong>
              <span style={appointmentTime}>
                {appointment.patientScheduledForText ||
                  formatAppointmentDate(appointment.scheduledForText)}
              </span>
              <p style={appointmentCopy}>
                WhatsApp care call
                {appointment.notes ? `: ${appointment.notes}` : ""}
              </p>
              {whatsappNumber && (
                <a
                  href={`https://wa.me/${whatsappNumber}`}
                  target="_blank"
                  rel="noreferrer"
                  style={appointmentAction}
                >
                  Open WhatsApp
                </a>
              )}
            </div>
          );
        })}
      </div>
    ) : (
      <p style={appointmentEmpty}>
        Appointment notifications from activated caregivers will appear here.
      </p>
    )}
  </div>
);

const AppointmentScheduler = ({
  worker,
  scheduledFor,
  notes,
  scheduling,
  message,
  onScheduledForChange,
  onNotesChange,
  onSubmit,
  onCancel,
  compact = false,
  panelStyle = appointmentPanel
}) => {
  const { date, hour, minute } = splitAppointmentDateTime(scheduledFor);

  const handleDateChange = (event) => {
    onScheduledForChange(buildAppointmentDateTime(event.target.value, hour, minute));
  };

  const handleHourChange = (event) => {
    onScheduledForChange(buildAppointmentDateTime(date, event.target.value, minute));
  };

  const handleMinuteChange = (event) => {
    onScheduledForChange(buildAppointmentDateTime(date, hour, event.target.value));
  };

  return (
    <form
      className={`appointment-scheduler-form${compact ? " appointment-scheduler-form-compact" : ""}`}
      style={panelStyle}
      onSubmit={onSubmit}
      onClick={(event) => event.stopPropagation()}
    >
      <div style={appointmentEyebrow}>Schedule care call</div>
      <h3 style={compact ? appointmentCompactTitle : appointmentTitle}>
        {worker ? `${worker.firstName} ${worker.lastName}` : "Selected caregiver"}
      </h3>
      <p style={compact ? appointmentCompactCopy : appointmentCopy}>
        Pick a local time. The caregiver sees the matching time in their country.
      </p>

      <div className="appointment-date-time-grid" style={compact ? appointmentCompactDateTimeGrid : appointmentDateTimeGrid}>
        <label style={appointmentDateField} htmlFor="elder-appointment-date">
          <span style={appointmentFormLabel}>Date</span>
          <input
            id="elder-appointment-date"
            type="date"
            style={compact ? appointmentCompactInput : input}
            value={date}
            onChange={handleDateChange}
          />
        </label>

        <label style={appointmentTimeField} htmlFor="elder-appointment-hour">
          <span style={appointmentFormLabel}>Hour</span>
          <select
            id="elder-appointment-hour"
            style={compact ? appointmentCompactInput : input}
            value={hour || DEFAULT_APPOINTMENT_HOUR}
            onChange={handleHourChange}
            disabled={!date}
          >
            {APPOINTMENT_HOURS.map((hourOption) => (
              <option key={hourOption} value={hourOption}>
                {hourOption}
              </option>
            ))}
          </select>
        </label>

        <label style={appointmentTimeField} htmlFor="elder-appointment-minute">
          <span style={appointmentFormLabel}>Minute</span>
          <select
            id="elder-appointment-minute"
            style={compact ? appointmentCompactInput : input}
            value={minute || DEFAULT_APPOINTMENT_MINUTE}
            onChange={handleMinuteChange}
            disabled={!date}
          >
            {APPOINTMENT_MINUTES.map((minuteOption) => (
              <option key={minuteOption} value={minuteOption}>
                {minuteOption}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label style={compact ? appointmentCompactNotesLabel : appointmentNotesLabel} htmlFor="elder-appointment-notes">
        Notes
      </label>
      <textarea
        id="elder-appointment-notes"
        style={compact ? appointmentCompactTextarea : { ...input, minHeight: "74px", resize: "vertical" }}
        value={notes}
        onChange={(event) => onNotesChange(event.target.value)}
        placeholder="Example: Please call after lunch"
      />

      <div style={compact ? appointmentCompactButtonRow : buttonRow}>
        <button type="submit" style={compact ? appointmentCompactApplyBtn : applyBtn} disabled={scheduling}>
          {scheduling ? "Scheduling..." : "Schedule"}
        </button>
        <button type="button" style={compact ? appointmentCompactClearBtn : clearBtn} onClick={onCancel}>
          Close
        </button>
      </div>

      {message && <div style={compact ? appointmentCompactErrorBox : activationErrorBox}>{message}</div>}
    </form>
  );
};

const IntroVideoModal = ({ worker, onClose }) => {
  if (!worker) return null;

  const embedUrl = getYouTubeEmbedUrl(worker.introVideoUrl);

  return (
    <div style={videoBackdrop} onClick={onClose}>
      <div style={videoModal} onClick={(event) => event.stopPropagation()}>
        <div style={videoHeader}>
          <div>
            <div style={appointmentEyebrow}>Caregiver intro</div>
            <h3 style={videoTitle}>
              {worker.firstName} {worker.lastName}
            </h3>
          </div>
          <button type="button" style={videoCloseButton} onClick={onClose}>
            Close
          </button>
        </div>
        {embedUrl ? (
          <>
            <iframe
              title={`${worker.firstName || "Caregiver"} introduction`}
              src={embedUrl}
              style={videoFrame}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
            <a
              href={worker.introVideoUrl}
              target="_blank"
              rel="noreferrer"
              style={videoExternalLink}
            >
              Open on YouTube
            </a>
          </>
        ) : (
          <a href={worker.introVideoUrl} target="_blank" rel="noreferrer" style={appointmentAction}>
            Open YouTube
          </a>
        )}
      </div>
    </div>
  );
};

const ElderMap = () => {
  const patientID = localStorage.getItem("patientID");
  const token = localStorage.getItem("token");
  const profileComplete = localStorage.getItem("profileComplete");
  const navigate = useNavigate();
  const { t } = useTranslation(["map", "common"]);
  const workerMarkerRefs = useRef({});

  const [draftFilters, setDraftFilters] = useState({
    language: "",
    gender: "",
    country: "",
    rate: ""
  });
  const [nameSearch, setNameSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [patient, setPatient] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [loadingWorkerID, setLoadingWorkerID] = useState(null);
  const [activationError, setActivationError] = useState("");
  const [expandedCountry, setExpandedCountry] = useState("");
  const [focusPoints, setFocusPoints] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointmentWorker, setSelectedAppointmentWorker] = useState(null);
  const [scheduledFor, setScheduledFor] = useState("");
  const [appointmentNotes, setAppointmentNotes] = useState("");
  const [schedulingAppointment, setSchedulingAppointment] = useState(false);
  const [appointmentMessage, setAppointmentMessage] = useState("");
  const [notificationAlertMessage, setNotificationAlertMessage] = useState("");
  const [urgentAppointmentID, setUrgentAppointmentID] = useState("");
  const [introVideoWorker, setIntroVideoWorker] = useState(null);
  const [selectedWorkerID, setSelectedWorkerID] = useState("");
  const alertedAppointmentsRef = useRef(readAlertedAppointments(ELDER_ALERT_STORAGE_KEY));
  const activeFilters = useMemo(() => Object.entries(filters), [filters]);

  useEffect(() => {
    if (!patientID || !token) {
      navigate("/elder-login");
      return;
    }

    if (profileComplete === "false") {
      navigate("/elder-profile-setup");
    }
  }, [navigate, patientID, profileComplete, token]);

  const renderStars = (rawRating) => {
    const rating = Math.max(0, Math.min(4, Number(rawRating) || 0));
    const maxStars = 4;

    return (
      <div style={{ color: "#d97706", fontSize: "18px", letterSpacing: "0.1em" }}>
        {"★".repeat(rating)}
        {"☆".repeat(maxStars - rating)}
      </div>
    );
  };

  const fetchMapData = useCallback(() => {
    axios
      .get(`${config.API_BASE_URL}/patient/map`, {
        params: { patientID, ...filters },
        headers: { Authorization: `Bearer ${token}` }
      })
      .then((res) => {
        setPatient(res.data.patient);
        setWorkers(res.data.workers);
      })
      .catch(() => {});
  }, [patientID, filters, token]);

  const fetchAppointments = useCallback(() => {
    if (!patientID || !token) return;

    axios
      .get(`${config.API_BASE_URL}/patient/appointments`, {
        params: { patientID },
        headers: { Authorization: `Bearer ${token}` }
      })
      .then((res) => {
        setAppointments(res.data.appointments || []);
      })
      .catch(() => {});
  }, [patientID, token]);

  useEffect(() => {
    fetchMapData();
    fetchAppointments();
    const interval = setInterval(fetchMapData, 5000);
    const appointmentInterval = setInterval(fetchAppointments, 15000);
    return () => {
      clearInterval(interval);
      clearInterval(appointmentInterval);
    };
  }, [fetchAppointments, fetchMapData]);

  useEffect(() => {
    if (!expandedCountry) return;

    const countryStillVisible = workers.some(
      (worker) => (worker.country || "Other") === expandedCountry
    );
    if (!countryStillVisible) {
      setExpandedCountry("");
    }
  }, [expandedCountry, workers]);

  const toggleActivation = async (workerID, isActive) => {
    try {
      setLoadingWorkerID(workerID);
      setActivationError("");

      const endpoint = isActive
        ? "/patient/deactivate-worker"
        : "/patient/activate-worker";

      await axios.post(
        `${config.API_BASE_URL}${endpoint}`,
        { patientID, workerID },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setWorkers((previousWorkers) =>
        previousWorkers.map((worker) =>
          worker.workerID === workerID
            ? { ...worker, activated: !isActive }
            : worker
        )
      );
      await fetchMapData();
      fetchAppointments();
      if (isActive && selectedAppointmentWorker?.workerID === workerID) {
        setSelectedAppointmentWorker(null);
      }
    } catch (err) {
      console.error(err);
      setActivationError(
        err.response?.data?.error ||
          "Unable to update this caregiver connection right now."
      );
    } finally {
      setLoadingWorkerID(null);
    }
  };

  const applyFilter = () => {
    setFilters(compactFilters(draftFilters));
    setExpandedCountry("");
    setFocusPoints([]);
    setSelectedWorkerID("");
  };

  const clearFilter = () => {
    setDraftFilters({
      language: "",
      gender: "",
      country: "",
      rate: ""
    });
    setNameSearch("");
    setFilters({});
    setExpandedCountry("");
    setFocusPoints([]);
    setSelectedWorkerID("");
  };

  const openScheduler = (worker) => {
    setSelectedAppointmentWorker(worker);
    setAppointmentMessage("");
    setSelectedWorkerID(worker.workerID);
    setFocusPoints([[Number(worker.latitude), Number(worker.longitude)]]);
  };

  const scheduleAppointment = async (event) => {
    event.preventDefault();

    if (!selectedAppointmentWorker?.workerID || !scheduledFor) {
      setAppointmentMessage("Choose a date and time for the WhatsApp call.");
      return;
    }

    try {
      setSchedulingAppointment(true);
      setAppointmentMessage("");

      await axios.post(
        `${config.API_BASE_URL}/appointments`,
        {
          workerID: selectedAppointmentWorker.workerID,
          patientID,
          scheduledFor: new Date(scheduledFor).toISOString(),
          scheduledForLocal: scheduledFor,
          title: "WhatsApp care call",
          notes: appointmentNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setScheduledFor("");
      setAppointmentNotes("");
      setAppointmentMessage("Appointment scheduled. The caregiver will see it in their queue.");
      fetchAppointments();
    } catch (err) {
      setAppointmentMessage(
        err.response?.data?.error || "Unable to schedule this appointment right now."
      );
    } finally {
      setSchedulingAppointment(false);
    }
  };

  const workerMarkers = workers.filter((worker) => worker.latitude && worker.longitude);
  const normalizedNameSearch = nameSearch.trim().toLowerCase();
  const nameSearchResults = useMemo(() => {
    if (!normalizedNameSearch) {
      return [];
    }

    return workerMarkers
      .map((worker) => {
        const displayName = getWorkerDisplayName(worker);
        const normalizedName = displayName.toLowerCase();
        const matchIndex = normalizedName.indexOf(normalizedNameSearch);

        if (matchIndex === -1) {
          return null;
        }

        return {
          worker,
          displayName,
          matchIndex,
          startsWithMatch: normalizedName.startsWith(normalizedNameSearch)
        };
      })
      .filter(Boolean)
      .sort(
        (left, right) =>
          Number(right.startsWithMatch) - Number(left.startsWithMatch) ||
          left.matchIndex - right.matchIndex ||
          left.displayName.localeCompare(right.displayName)
      )
      .slice(0, 5);
  }, [normalizedNameSearch, workerMarkers]);
  const countryClusters = buildCountryClusters(workerMarkers);
  const visibleWorkers = expandedCountry
    ? workerMarkers.filter((worker) => (worker.country || "Other") === expandedCountry)
    : [];
  const overviewMarkers = spreadOverlappingMarkers([
    ...countryClusters.map((cluster) => ({
      ...cluster,
      markerType: "cluster",
      markerID: `country-${cluster.country}`
    })),
    ...(patient?.latitude && patient?.longitude
      ? [
          {
            ...patient,
            markerType: "patient",
            markerID: "patient-home"
          }
        ]
      : [])
  ]);
  const expandedMarkers = spreadOverlappingMarkers([
    ...visibleWorkers.map((worker) => ({
      ...worker,
      markerType: "worker",
      markerID: worker.workerID
    })),
    ...(patient?.latitude && patient?.longitude
      ? [
          {
            ...patient,
            markerType: "patient",
            markerID: "patient-home"
          }
        ]
      : [])
  ]);
  const overviewCountryClusters = overviewMarkers.filter((marker) => marker.markerType === "cluster");
  const expandedWorkerMarkers = expandedMarkers.filter((marker) => marker.markerType === "worker");
  const expandedPatientMarker = expandedMarkers.find((marker) => marker.markerType === "patient");
  const overviewPatientMarker = overviewMarkers.find((marker) => marker.markerType === "patient");
  const overlapGuides = buildOverlapGuides(expandedWorkerMarkers);
  const appointmentNotifications = useMemo(
    () => appointments.filter((appointment) => appointment.status !== "cancelled"),
    [appointments]
  );

  useEffect(() => {
    const dueAppointment = appointmentNotifications.find((appointment) => {
      const status = getAppointmentAlertStatus(appointment);
      return status === "now" && !alertedAppointmentsRef.current.has(appointment.appointmentID);
    });

    const urgentAppointment = appointmentNotifications.find(
      (appointment) => getAppointmentAlertStatus(appointment) === "now"
    );

    setUrgentAppointmentID(urgentAppointment?.appointmentID || "");

    if (!dueAppointment) return;

    rememberAlertedAppointment(
      ELDER_ALERT_STORAGE_KEY,
      dueAppointment.appointmentID,
      alertedAppointmentsRef.current
    );
    const alertText = createAppointmentAlertMessage(dueAppointment);
    setNotificationAlertMessage(alertText);

    if ("Notification" in window && Notification.permission === "granted") {
      new Notification("UberCARE appointment now", {
        body: alertText,
        tag: dueAppointment.appointmentID
      });
      return;
    }

    window.alert(alertText);
  }, [appointmentNotifications]);

  useEffect(() => {
    if (!selectedWorkerID) return;

    const marker = workerMarkerRefs.current[selectedWorkerID];
    if (!marker) return;

    const timer = window.setTimeout(() => marker.openPopup(), 160);
    return () => window.clearTimeout(timer);
  }, [selectedWorkerID, expandedWorkerMarkers]);

  const handleCountryClusterClick = (cluster) => {
    setExpandedCountry(cluster.country);
    setFocusPoints(cluster.members.map((worker) => [worker.latitude, worker.longitude]));
  };

  const handleNameSearchSelect = (worker) => {
    setNameSearch(getWorkerDisplayName(worker));
    setExpandedCountry(worker.country || "Other");
    setSelectedWorkerID(worker.workerID);
    setFocusPoints([[Number(worker.latitude), Number(worker.longitude)]]);
  };

  const resetCountryView = () => {
    setExpandedCountry("");
    setSelectedWorkerID("");
    const allPoints = [
      ...countryClusters.map((cluster) => [cluster.latitude, cluster.longitude]),
      ...(patient?.latitude && patient?.longitude ? [[patient.latitude, patient.longitude]] : [])
    ];
    setFocusPoints(allPoints);
  };

  return (
    <>
      <style>{popupStyles}</style>

      <div style={page}>
        <div style={layout} className="elder-map-layout">
        <aside className="elder-map-filter-panel" style={filterPanel}>
          <div className="elder-map-filter-header" style={headerRow}>
            <button type="button" style={backBtn} onClick={() => navigate(-1)}>
              {t("map:map.btnBack")}
            </button>
            <div>
              <div style={panelEyebrow}>Caregiver discovery</div>
              <h3 style={filterTitle}>{t("map:map.filterSmartTitle")}</h3>
            </div>
          </div>

          <div style={field}>
            <label style={label} htmlFor="elder-map-name-search">
              Search by caregiver name
            </label>
            <input
              id="elder-map-name-search"
              type="text"
              style={input}
              value={nameSearch}
              onChange={(event) => setNameSearch(event.target.value)}
              placeholder="Type a first or last name"
              autoComplete="off"
            />

            {normalizedNameSearch && (
              <div style={nameSearchResultsPanel}>
                {nameSearchResults.length ? (
                  nameSearchResults.map(({ worker, displayName }) => (
                    <button
                      key={worker.workerID}
                      type="button"
                      style={nameSearchResultButton}
                      onClick={() => handleNameSearchSelect(worker)}
                    >
                      <span style={nameSearchResultName}>{displayName}</span>
                      <span style={nameSearchResultMeta}>
                        {getLocationLabel(worker) || worker.country || "Pinned map location"}
                      </span>
                    </button>
                  ))
                ) : (
                  <div style={nameSearchEmpty}>No caregiver found with that name.</div>
                )}
              </div>
            )}
          </div>

          <div style={filterBlock}>
            <div style={filterBlockHeader}>
              <span style={panelEyebrow}>Filters</span>
              <span style={filterHelpText}>Choose one or many, then apply.</span>
            </div>

            <div style={filterGrid}>
              {FILTER_TYPES.map((filter) => (
                <label key={filter.value} style={filterField}>
                  <span style={label}>{filter.label}</span>
                  <select
                    style={input}
                    value={draftFilters[filter.value] || ""}
                    onChange={(event) =>
                      setDraftFilters((previous) => ({
                        ...previous,
                        [filter.value]: event.target.value
                      }))
                    }
                  >
                    <option value="">{t("map:map.filterSelect")}</option>
                    {getFilterOptions(filter.value).map((valueOption) => (
                      <option key={valueOption.value} value={valueOption.value}>
                        {valueOption.label}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          </div>

          <div style={buttonRow}>
            <button type="button" style={applyBtn} onClick={applyFilter}>
              {t("common:buttons.apply")}
            </button>
            <button type="button" style={clearBtn} onClick={clearFilter}>
              {t("common:buttons.clear")}
            </button>
          </div>

          {activeFilters.length > 0 && (
            <div style={activeFilterPanel}>
              <div style={activeFilterTitle}>Active filters</div>
              <div style={activeFilterList}>
                {activeFilters.map(([filterKey, filterValue]) => (
                  <span key={filterKey} style={activeFilterChip}>
                    {FILTER_TYPES.find((filter) => filter.value === filterKey)?.label || filterKey}:{" "}
                    {getFilterValueLabel(filterKey, filterValue)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {activationError && <div style={activationErrorBox}>{activationError}</div>}

          {expandedCountry && (
            <div style={clusterInfoCard}>
              <strong style={clusterInfoTitle}>{expandedCountry}</strong>
              <p style={clusterInfoText}>
                Showing {visibleWorkers.length} caregiver{visibleWorkers.length === 1 ? "" : "s"} in this country.
              </p>
              <button type="button" style={clusterResetBtn} onClick={resetCountryView}>
                Show all country markers
              </button>
            </div>
          )}

          <AppointmentNotifications
            appointments={appointmentNotifications}
            urgentAppointmentID={urgentAppointmentID}
            alertMessage={notificationAlertMessage}
          />
        </aside>

        <section style={mapFrame}>
          <MapContainer
            center={[20, 0]}
            zoom={2}
            minZoom={2}
            zoomControl={false}
            style={mapStyle}
            className="elder-map-canvas"
          >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />

          <ZoomControls />
          <MapViewportController focusPoints={focusPoints} />

          {!expandedCountry &&
            overviewCountryClusters.map((cluster) => (
              <Marker
                key={`country-${cluster.country}`}
                position={[cluster.markerLatitude, cluster.markerLongitude]}
                icon={createCountryClusterIcon(cluster.country, cluster.members.length)}
                zIndexOffset={1200}
                eventHandlers={{
                  click: () => handleCountryClusterClick(cluster)
                }}
              >
                <Popup maxWidth={280} className="worker-map-popup">
                  <div className="country-cluster-popup">
                    <strong>{cluster.country}</strong>
                    <p>
                      Number shown on the marker: {cluster.members.length} caregiver{cluster.members.length === 1 ? "" : "s"} in {cluster.country}.
                    </p>
                    <p>
                      Abbreviation shown: {getCountryAbbreviation(cluster.country)}. Click the purple marker to spread caregivers across the country.
                    </p>
                  </div>
                </Popup>
              </Marker>
            ))}

          {expandedCountry &&
            overlapGuides.map((group) => (
              <Fragment key={`overlap-${group.key}`}>
                {group.members.map((worker) => (
                  <Polyline
                    key={`overlap-line-${worker.workerID}`}
                    positions={[group.center, [worker.markerLatitude, worker.markerLongitude]]}
                    pathOptions={{
                      color: worker.status === "available" ? "#16a34a" : "#dc2626",
                      weight: 2,
                      opacity: 0.55,
                      dashArray: "3 5"
                    }}
                  />
                ))}

                <CircleMarker
                  center={group.center}
                  radius={Math.min(10 + group.count, 16)}
                  pathOptions={{
                    color: "#0f5f58",
                    weight: 2,
                    fillColor: "#ffffff",
                    fillOpacity: 0.94
                  }}
                >
                  <Popup maxWidth={260} className="worker-map-popup">
                    <div className="country-cluster-popup">
                      <strong>Shared caregiver location</strong>
                      <p>
                        {group.count} caregivers share this same saved map point. Their markers are spread in a circle so they are easier to tap.
                      </p>
                    </div>
                  </Popup>
                </CircleMarker>
              </Fragment>
            ))}

          {expandedCountry &&
            expandedWorkerMarkers.map((worker) => (
              <Marker
                key={worker.workerID}
                position={[worker.markerLatitude, worker.markerLongitude]}
                icon={worker.status === "available" ? greenIcon : redIcon}
                zIndexOffset={worker.workerID === selectedWorkerID ? 1300 : 0}
                ref={(marker) => {
                  if (marker) {
                    workerMarkerRefs.current[worker.workerID] = marker;
                  } else {
                    delete workerMarkerRefs.current[worker.workerID];
                  }
                }}
              >
                <Popup maxWidth={420} className="worker-map-popup">
                  <WorkerPopup
                    worker={worker}
                    loadingWorkerID={loadingWorkerID}
                    onToggleActivation={toggleActivation}
                    onSchedule={openScheduler}
                    schedulerWorkerID={selectedAppointmentWorker?.workerID || ""}
                    schedulerProps={{
                      scheduledFor,
                      notes: appointmentNotes,
                      scheduling: schedulingAppointment,
                      message: appointmentMessage,
                      onScheduledForChange: setScheduledFor,
                      onNotesChange: setAppointmentNotes,
                      onSubmit: scheduleAppointment,
                      onCancel: () => {
                        setSelectedAppointmentWorker(null);
                        setAppointmentMessage("");
                      }
                    }}
                    onWatchIntro={setIntroVideoWorker}
                    renderStars={renderStars}
                    t={t}
                  />
                </Popup>
              </Marker>
            ))}

          {!expandedCountry && overviewPatientMarker && (
            <Marker
              position={[overviewPatientMarker.markerLatitude, overviewPatientMarker.markerLongitude]}
              icon={blackIcon}
              zIndexOffset={200}
            >
              <Popup maxWidth={360} className="worker-map-popup">
                <PatientPopup patient={overviewPatientMarker} t={t} />
              </Popup>
            </Marker>
          )}

          {expandedCountry && expandedPatientMarker && (
            <Marker
              position={[expandedPatientMarker.markerLatitude, expandedPatientMarker.markerLongitude]}
              icon={blackIcon}
            >
              <Popup maxWidth={360} className="worker-map-popup">
                <PatientPopup patient={expandedPatientMarker} t={t} />
              </Popup>
            </Marker>
          )}
          </MapContainer>
        </section>
        <IntroVideoModal
          worker={introVideoWorker}
          onClose={() => setIntroVideoWorker(null)}
        />
        </div>
      </div>
    </>
  );
};

export default ElderMap;

const formatAppointmentDate = (value) => {
  if (!value) return "Time not set";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return parsed.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const page = {
  minHeight: "100vh",
  padding: "18px",
  background: "linear-gradient(180deg, #edf4f8 0%, #dfe9f2 100%)",
  fontSize: "16px"
};

const layout = {
  display: "grid",
  gridTemplateColumns: "minmax(320px, 380px) minmax(0, 1fr)",
  gap: "18px",
  alignItems: "start",
  maxWidth: "1500px",
  margin: "0 auto"
};

const mapFrame = {
  background: "rgba(255, 255, 255, 0.86)",
  padding: "12px",
  borderRadius: "20px",
  border: "1px solid rgba(14, 85, 128, 0.14)",
  boxShadow: "0 22px 48px rgba(17, 44, 77, 0.14)"
};

const mapStyle = {
  height: "calc(100vh - 60px)",
  minHeight: "620px",
  width: "100%",
  borderRadius: "14px",
  zIndex: 1
};

const filterPanel = {
  position: "sticky",
  top: "18px",
  maxHeight: "calc(100vh - 36px)",
  overflowY: "auto",
  padding: "18px",
  background: "linear-gradient(180deg, #0e5580 0%, #156893 100%)",
  border: "1px solid rgba(255, 208, 63, 0.16)",
  borderRadius: "20px",
  boxShadow: "0 22px 48px rgba(12, 42, 68, 0.16)",
  zIndex: 10
};

const headerRow = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "16px"
};

const backBtn = {
  minHeight: "42px",
  padding: "0 14px",
  border: "none",
  background: "#ffffff",
  color: "#0E5580",
  borderRadius: "12px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "900",
  boxShadow: "0 10px 20px rgba(0,0,0,0.12)"
};

const panelEyebrow = {
  color: "#ffe07d",
  fontSize: "11px",
  fontWeight: "900",
  letterSpacing: "0.08em",
  textTransform: "uppercase"
};

const filterTitle = {
  margin: 0,
  fontSize: "20px",
  fontWeight: "900",
  color: "#ffffff"
};

const field = {
  marginBottom: "14px",
  display: "flex",
  flexDirection: "column"
};

const label = {
  fontSize: "13px",
  fontWeight: "800",
  marginBottom: "6px",
  color: "#e5f3fb"
};

const input = {
  minHeight: "44px",
  padding: "10px 12px",
  fontSize: "15px",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  borderRadius: "12px",
  background: "#f8fbfe",
  color: "#123a59",
  minWidth: 0,
  width: "100%"
};

const appointmentCompactInput = {
  ...input,
  minHeight: "36px",
  padding: "7px 9px",
  fontSize: "14px",
  borderRadius: "10px"
};

const appointmentCompactTextarea = {
  ...appointmentCompactInput,
  minHeight: "44px",
  resize: "vertical"
};

const filterBlock = {
  marginTop: "8px",
  padding: "14px",
  borderRadius: "16px",
  background: "rgba(255, 255, 255, 0.1)",
  border: "1px solid rgba(255, 255, 255, 0.12)"
};

const filterBlockHeader = {
  display: "flex",
  justifyContent: "space-between",
  gap: "10px",
  alignItems: "center",
  marginBottom: "12px"
};

const filterHelpText = {
  color: "rgba(255, 255, 255, 0.78)",
  fontSize: "12px",
  fontWeight: "700",
  textAlign: "right"
};

const filterGrid = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "12px"
};

const filterField = {
  display: "flex",
  flexDirection: "column"
};

const appointmentDateTimeGrid = {
  display: "grid",
  gridTemplateColumns: "minmax(150px, 1fr) minmax(76px, 92px) minmax(82px, 98px)",
  gap: "10px",
  alignItems: "end"
};

const appointmentCompactDateTimeGrid = {
  ...appointmentDateTimeGrid,
  gridTemplateColumns: "minmax(132px, 1fr) minmax(66px, 76px) minmax(72px, 82px)",
  gap: "8px"
};

const appointmentDateField = {
  display: "flex",
  flexDirection: "column",
  minWidth: 0
};

const appointmentTimeField = {
  display: "flex",
  flexDirection: "column",
  minWidth: "76px"
};

const appointmentFormLabel = {
  ...label,
  color: "#102a43"
};

const appointmentNotesLabel = {
  ...appointmentFormLabel,
  display: "block",
  marginTop: "12px"
};

const appointmentCompactNotesLabel = {
  ...appointmentNotesLabel,
  marginTop: "8px",
  marginBottom: "4px"
};

const nameSearchResultsPanel = {
  marginTop: "8px",
  borderRadius: "10px",
  border: "1px solid rgba(14, 85, 128, 0.16)",
  overflow: "hidden",
  background: "#ffffff"
};

const nameSearchResultButton = {
  width: "100%",
  border: "none",
  borderBottom: "1px solid rgba(14, 85, 128, 0.1)",
  background: "#ffffff",
  color: "#102a43",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "3px",
  padding: "10px",
  textAlign: "left"
};

const nameSearchResultName = {
  fontSize: "14px",
  fontWeight: "800"
};

const nameSearchResultMeta = {
  color: "#486581",
  fontSize: "12px",
  lineHeight: 1.35
};

const nameSearchEmpty = {
  color: "#486581",
  fontSize: "13px",
  padding: "10px"
};

const buttonRow = {
  display: "flex",
  gap: "10px",
  marginTop: "15px"
};

const appointmentCompactButtonRow = {
  ...buttonRow,
  gap: "8px",
  marginTop: "10px"
};

const activeFilterPanel = {
  marginTop: "14px",
  padding: "12px",
  borderRadius: "14px",
  background: "rgba(255, 255, 255, 0.12)",
  border: "1px solid rgba(255, 255, 255, 0.16)"
};

const activeFilterTitle = {
  color: "#ffffff",
  fontSize: "13px",
  fontWeight: "900",
  marginBottom: "8px"
};

const activeFilterList = {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px"
};

const activeFilterChip = {
  borderRadius: "999px",
  background: "#ffffff",
  color: "#0e5580",
  padding: "7px 10px",
  fontSize: "12px",
  fontWeight: "900"
};

const clusterInfoCard = {
  marginTop: "14px",
  padding: "14px",
  borderRadius: "14px",
  background: "rgba(91, 33, 182, 0.08)",
  border: "1px solid rgba(91, 33, 182, 0.18)"
};

const clusterInfoTitle = {
  display: "block",
  color: "#4c1d95",
  fontSize: "15px",
  marginBottom: "4px"
};

const clusterInfoText = {
  margin: "0 0 10px",
  color: "#5b4b78",
  fontSize: "13px",
  lineHeight: 1.5
};

const clusterResetBtn = {
  width: "100%",
  minHeight: "40px",
  borderRadius: "12px",
  border: "none",
  background: "linear-gradient(135deg, #6d28d9, #8b5cf6)",
  color: "#fff",
  cursor: "pointer",
  fontWeight: "700"
};

const activationErrorBox = {
  marginTop: "12px",
  padding: "10px 12px",
  borderRadius: "12px",
  background: "#fff1f0",
  border: "1px solid #f2c6c2",
  color: "#b42318",
  fontSize: "13px",
  lineHeight: 1.45
};

const appointmentCompactErrorBox = {
  ...activationErrorBox,
  marginTop: "8px",
  padding: "8px 10px",
  fontSize: "12px",
  lineHeight: 1.35
};

const appointmentPanel = {
  marginTop: "14px",
  padding: "14px",
  borderRadius: "14px",
  background: "#ffffff",
  border: "1px solid rgba(14, 85, 128, 0.14)",
  boxShadow: "0 14px 30px rgba(15, 23, 42, 0.12)",
  cursor: "default"
};

const popupAppointmentPanel = {
  ...appointmentPanel,
  gridColumn: "1 / -1",
  marginTop: 0,
  padding: "10px",
  borderRadius: "12px",
  background: "#f8fbfd",
  boxShadow: "none"
};

const appointmentEyebrow = {
  color: "#0f5f58",
  fontSize: "11px",
  fontWeight: "900",
  letterSpacing: "0.08em",
  textTransform: "uppercase"
};

const appointmentTitle = {
  margin: "4px 0 10px",
  color: "#102a43",
  fontSize: "16px",
  fontWeight: "900"
};

const appointmentCompactTitle = {
  ...appointmentTitle,
  margin: "2px 0 6px",
  fontSize: "15px"
};

const appointmentList = {
  display: "grid",
  gap: "10px"
};

const appointmentCard = {
  padding: "12px",
  borderRadius: "12px",
  background: "rgba(15, 95, 88, 0.07)",
  border: "1px solid rgba(15, 95, 88, 0.12)",
  display: "grid",
  gap: "6px"
};

const urgentAppointmentCard = {
  border: "2px solid #f97316",
  background: "linear-gradient(180deg, #fff7ed 0%, #ffffff 100%)",
  boxShadow: "0 16px 32px rgba(249, 115, 22, 0.18)"
};

const soonAppointmentCard = {
  border: "1px solid #facc15",
  background: "linear-gradient(180deg, #fefce8 0%, #ffffff 100%)"
};

const urgentBadge = {
  justifySelf: "start",
  borderRadius: "999px",
  background: "#f97316",
  color: "#ffffff",
  padding: "5px 9px",
  fontSize: "11px",
  fontWeight: "900",
  letterSpacing: "0.04em",
  textTransform: "uppercase"
};

const soonBadge = {
  ...urgentBadge,
  background: "#ca8a04"
};

const appointmentAlertBox = {
  borderRadius: "12px",
  background: "#fff7ed",
  color: "#9a3412",
  padding: "10px 12px",
  fontSize: "13px",
  lineHeight: 1.45,
  border: "1px solid #fed7aa",
  fontWeight: "900"
};

const enableAlertsButton = {
  minHeight: "40px",
  borderRadius: "12px",
  border: "1px solid rgba(14, 85, 128, 0.16)",
  background: "#0e5580",
  color: "#ffffff",
  cursor: "pointer",
  fontWeight: "900",
  padding: "0 14px"
};

const appointmentCaregiver = {
  display: "block",
  color: "#102a43",
  fontSize: "14px"
};

const appointmentTime = {
  display: "block",
  marginTop: "4px",
  color: "#0f5f58",
  fontSize: "13px",
  fontWeight: "900"
};

const appointmentCopy = {
  margin: "8px 0 0",
  color: "#486581",
  fontSize: "13px",
  lineHeight: 1.45
};

const appointmentCompactCopy = {
  ...appointmentCopy,
  margin: "0 0 8px",
  fontSize: "12px",
  lineHeight: 1.35
};

const appointmentAction = {
  marginTop: "10px",
  minHeight: "36px",
  borderRadius: "10px",
  background: "#25d366",
  color: "#ffffff",
  textDecoration: "none",
  fontWeight: "900",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 12px",
  fontSize: "13px"
};

const appointmentEmpty = {
  color: "#486581",
  fontSize: "13px",
  lineHeight: 1.5,
  margin: 0
};

const videoBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.62)",
  zIndex: 4000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px"
};

const videoModal = {
  width: "min(860px, 96vw)",
  background: "#ffffff",
  borderRadius: "14px",
  padding: "16px",
  boxShadow: "0 28px 70px rgba(15, 23, 42, 0.34)"
};

const videoHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "14px",
  marginBottom: "12px"
};

const videoTitle = {
  margin: "3px 0 0",
  color: "#102a43",
  fontSize: "18px",
  fontWeight: "900"
};

const videoCloseButton = {
  minHeight: "36px",
  borderRadius: "10px",
  border: "1px solid rgba(14, 85, 128, 0.18)",
  background: "#ffffff",
  color: "#0e5580",
  cursor: "pointer",
  fontWeight: "900",
  padding: "0 12px"
};

const videoFrame = {
  width: "100%",
  aspectRatio: "16 / 9",
  border: 0,
  borderRadius: "10px",
  background: "#0f172a"
};

const videoExternalLink = {
  marginTop: "12px",
  minHeight: "40px",
  borderRadius: "10px",
  background: "#0e5580",
  color: "#ffffff",
  textDecoration: "none",
  fontWeight: "900",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 14px",
  fontSize: "13px"
};

const applyBtn = {
  flex: 1,
  padding: "12px",
  background: "linear-gradient(135deg,#0E5580,#1c7ed6)",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  fontWeight: "600"
};

const appointmentCompactApplyBtn = {
  ...applyBtn,
  minHeight: "38px",
  padding: "8px 10px"
};

const clearBtn = {
  flex: 1,
  padding: "12px",
  background: "#6c757d",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer"
};

const appointmentCompactClearBtn = {
  ...clearBtn,
  minHeight: "38px",
  padding: "8px 10px"
};

const zoomControls = {
  position: "absolute",
  top: "18px",
  right: "18px",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  zIndex: 2000
};

const zoomBtn = {
  width: "55px",
  height: "55px",
  fontSize: "24px",
  fontWeight: "800",
  borderRadius: "14px",
  border: "none",
  background: "#ffffff",
  boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
  cursor: "pointer"
};

const popupStyles = `
.elder-map-canvas {
  background: #dbeafe;
}

.care-map-marker-shell {
  background: transparent;
  border: none;
}

.care-map-marker {
  position: relative;
  width: 46px;
  height: 46px;
  border-radius: 16px 16px 16px 4px;
  transform: rotate(-45deg);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid #ffffff;
  box-shadow: 0 14px 26px rgba(15, 23, 42, 0.24);
}

.care-map-marker::after {
  content: "";
  position: absolute;
  inset: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
}

.care-map-marker span {
  position: relative;
  z-index: 1;
  transform: rotate(45deg);
  color: #ffffff;
  font-size: 0.88rem;
  font-weight: 900;
  line-height: 1;
}

.care-map-marker-available {
  background: linear-gradient(135deg, #15803d, #22c55e);
}

.care-map-marker-unavailable {
  background: linear-gradient(135deg, #b91c1c, #ef4444);
}

.care-map-marker-elder {
  background: linear-gradient(135deg, #111827, #475569);
}

@media (max-width: 980px) {
  .elder-map-layout {
    grid-template-columns: 1fr !important;
  }

  .elder-map-filter-panel {
    position: static !important;
    max-height: none !important;
  }

  .elder-map-canvas {
    height: 72svh !important;
    min-height: 460px !important;
  }
}

@media (max-width: 620px) {
  .elder-map-filter-header {
    align-items: flex-start !important;
    flex-direction: column !important;
  }

  .worker-map-popup .leaflet-popup-content {
    min-width: min(82vw, 280px) !important;
  }

  .worker-popup-grid,
  .worker-popup-hero {
    grid-template-columns: 1fr !important;
  }
}

.country-cluster-icon-wrapper {
  background: transparent;
  border: none;
}

.country-cluster-icon {
  width: 62px;
  height: 62px;
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #6d28d9, #a855f7);
  color: #fff;
  border: 3px solid rgba(255, 255, 255, 0.95);
  box-shadow: 0 14px 24px rgba(91, 33, 182, 0.32);
  gap: 0.08rem;
  padding: 0.28rem 0.2rem;
}

.country-cluster-code {
  font-weight: 900;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  line-height: 1;
}

.country-cluster-count {
  font-weight: 900;
  font-size: 1.05rem;
  line-height: 1;
}

.country-cluster-caption {
  font-weight: 700;
  font-size: 0.46rem;
  line-height: 1;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

@media (max-width: 720px) {
  .country-cluster-icon {
    width: 56px;
    height: 56px;
  }

  .country-cluster-count {
    font-size: 0.96rem;
  }

  .country-cluster-caption {
    font-size: 0.42rem;
  }
}

.country-cluster-popup {
  padding: 0.35rem;
  color: #2e1065;
}

.country-cluster-popup strong {
  display: block;
  font-size: 1rem;
}

.country-cluster-popup p {
  margin: 0.35rem 0 0;
  font-size: 0.85rem;
  line-height: 1.45;
}

.worker-map-popup .leaflet-popup-content-wrapper {
  padding: 0;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 24px 45px rgba(15, 23, 42, 0.18);
}

.worker-map-popup .leaflet-popup-content {
  margin: 0;
  width: min(90vw, 420px) !important;
  min-width: 0;
}

.worker-map-popup .leaflet-popup-tip {
  width: 18px;
  height: 18px;
  box-shadow: none;
}

.worker-popup-card {
  padding: 0.72rem;
  background:
    radial-gradient(circle at top right, rgba(216, 140, 58, 0.15), transparent 34%),
    linear-gradient(180deg, #ffffff, #f7fbfc);
  color: #102a43;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.55rem;
  max-height: none;
  overflow: visible;
}

.worker-popup-hero {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.7rem;
  align-items: center;
  grid-column: 1 / -1;
}

.worker-avatar {
  width: 48px;
  height: 48px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f5f58, #2f8f85);
  color: #fff;
  font-weight: 800;
  box-shadow: 0 14px 26px rgba(15, 95, 88, 0.18);
}

.worker-photo {
  object-fit: cover;
}

.patient-avatar {
  font-size: 0.86rem;
  background: linear-gradient(135deg, #1f2937, #475569);
}

.worker-name {
  font-size: 0.98rem;
  font-weight: 800;
}

.worker-role {
  margin-top: 0.2rem;
  font-size: 0.8rem;
  color: #627d98;
}

.worker-status-chip {
  padding: 0.38rem 0.65rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 800;
  white-space: nowrap;
}

.worker-popup-grid {
  grid-column: 1 / -1;
  display: flex;
  gap: 0.55rem;
  margin-top: 0;
  overflow-x: auto;
  overscroll-behavior-inline: contain;
  padding-bottom: 0.18rem;
  scrollbar-width: thin;
}

.worker-popup-grid-expanded {
  margin-top: 0.8rem;
}

.worker-popup-stat {
  flex: 0 0 136px;
  min-height: 74px;
  padding: 0.62rem;
  border-radius: 13px;
  background: rgba(15, 95, 88, 0.06);
  border: 1px solid rgba(15, 95, 88, 0.08);
}

.worker-popup-stat span,
.worker-popup-label {
  display: block;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #486581;
}

.worker-popup-stat strong {
  display: block;
  margin-top: 0.28rem;
  font-size: 0.82rem;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.worker-popup-location-note {
  display: block;
  margin-top: 0.35rem;
  color: #6b7280;
  font-size: 0.72rem;
  line-height: 1.4;
}

.worker-popup-section {
  grid-column: span 2;
  margin-top: 0;
}

.worker-popup-text {
  margin-top: 0.28rem;
  color: #243b53;
  line-height: 1.5;
  font-size: 0.88rem;
}

.worker-popup-list {
  margin-top: 0.32rem;
  display: grid;
  gap: 0.42rem;
}

.worker-popup-list-item {
  padding-left: 0.85rem;
  position: relative;
  color: #243b53;
  line-height: 1.45;
  font-size: 0.86rem;
}

.worker-popup-list-item::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.48rem;
  width: 0.36rem;
  height: 0.36rem;
  border-radius: 999px;
  background: #0f5f58;
}

.worker-rating-row {
  margin-top: 0.28rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.75rem;
}

.worker-rating-copy {
  font-size: 0.82rem;
  color: #627d98;
}

.worker-more-toggle {
  display: none;
  margin-top: 0.85rem;
  width: 100%;
  min-height: 46px;
  padding: 0.8rem 0.95rem;
  border: 1px solid rgba(15, 95, 88, 0.18);
  border-radius: 14px;
  background: rgba(15, 95, 88, 0.08);
  color: #0f5f58;
  font-size: 0.88rem;
  font-weight: 800;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  transition: background 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
}

.worker-more-toggle:hover,
.worker-more-toggle:focus-visible {
  background: rgba(15, 95, 88, 0.14);
  border-color: rgba(15, 95, 88, 0.28);
  transform: translateY(-1px);
}

.worker-more-toggle-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 999px;
  background: rgba(15, 95, 88, 0.12);
  font-size: 0.84rem;
  line-height: 1;
}

.worker-more-toggle-icon.is-open {
  background: rgba(15, 95, 88, 0.18);
}

.worker-primary-action,
.worker-secondary-action {
  width: 100%;
  margin-top: 0;
  min-height: 44px;
  border: none;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-weight: 800;
  font-size: 0.86rem;
  cursor: pointer;
}

.worker-primary-action {
  background: linear-gradient(135deg, #25d366, #159947);
  color: #fff;
  box-shadow: 0 16px 28px rgba(21, 153, 71, 0.18);
}

.worker-secondary-action {
  background: linear-gradient(135deg, #0e5580, #1c7ed6);
  color: #fff;
  box-shadow: 0 16px 28px rgba(15, 23, 42, 0.12);
}

.worker-popup-card > .worker-primary-action {
  grid-column: span 2;
}

.worker-popup-card > .worker-secondary-action {
  grid-column: span 1;
}

.worker-popup-card > .appointment-scheduler-form {
  grid-column: 1 / -1;
}

.appointment-scheduler-form-compact {
  align-self: start;
}

.appointment-scheduler-form-compact textarea {
  max-height: 76px;
}

.worker-disabled {
  background: #cbd5e1;
  color: #475569;
  box-shadow: none;
  cursor: not-allowed;
}

@media (max-width: 980px) {
  .worker-map-popup .leaflet-popup-content {
    min-width: 245px;
  }
}

@media (max-width: 720px) {
  .elder-map-filter-panel {
    position: fixed !important;
    left: 0.5rem !important;
    right: 0.5rem !important;
    bottom: calc(0.5rem + env(safe-area-inset-bottom)) !important;
    top: auto !important;
    width: auto !important;
    max-width: none !important;
    max-height: min(54svh, 470px) !important;
    padding: 0.85rem !important;
    border-radius: 18px !important;
    cursor: default !important;
  }

  .elder-map-filter-header {
    cursor: default !important;
  }

  .elder-map-filter-panel select,
  .elder-map-filter-panel input,
  .elder-map-filter-panel button,
  .elder-map-filter-panel textarea {
    font-size: 16px !important;
  }

  .leaflet-control-attribution {
    max-width: calc(100vw - 1rem);
    white-space: normal;
  }

  .worker-map-popup .leaflet-popup-content-wrapper {
    border-radius: 18px;
  }

  .worker-map-popup .leaflet-popup-content {
    min-width: 0;
    width: min(92vw, 340px) !important;
  }

  .worker-popup-card {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    max-height: none;
    overflow: visible;
    padding: 0.72rem;
    gap: 0.55rem;
  }

  .worker-popup-hero {
    grid-template-columns: auto 1fr;
  }

  .worker-status-chip {
    grid-column: 1 / -1;
    justify-self: start;
  }

  .worker-popup-grid {
    display: flex;
  }

  .worker-rating-row {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.3rem;
  }

  .worker-popup-section {
    grid-column: 1 / -1;
  }

  .worker-popup-stat {
    flex-basis: 126px;
    min-height: 68px;
  }

  .worker-popup-card > .worker-primary-action,
  .worker-popup-card > .worker-secondary-action {
    grid-column: span 1;
    min-height: 42px;
    font-size: 0.78rem;
    padding-inline: 0.45rem;
  }

  .appointment-date-time-grid {
    grid-template-columns: 1fr !important;
  }

  .appointment-scheduler-form-compact .appointment-date-time-grid {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
  }

  .appointment-scheduler-form-compact .appointment-date-time-grid > label:first-child {
    grid-column: 1 / -1;
  }
}

@media (max-width: 420px) {
  .elder-map-filter-panel {
    max-height: min(48svh, 390px) !important;
  }

  .worker-map-popup .leaflet-popup-content {
    width: min(84vw, 292px) !important;
  }
}
`;
